"use client";

import React, { useCallback, useEffect, useState, useRef, memo, EffectCallback, act, useMemo, use } from "react";
import { chatCreateArtifact, getChat, resetChat } from "@/api/sdk.gen";
import { chatSession, SessionCommandError, SessionMessage } from "@/aawebsocket/chatSession";
import { runsState } from "@/app/runsState";
import { isUsageBlockReason, UsageBlockReason, usageMeterState } from "@/app/usageMeterState";
import { applySessionMessage, isScopedSessionMessage, routeSend } from "./applyRunEvent";
import { Chat } from "./chatWindow/chat";
import { ChatArtifact, chatState, ScrollToObjectKeyDetail } from "@/app/chatState";
import { appState } from "@/app/appState";
import { ChatMessage, ChatMessageBlockUserAction, ConversationBlock } from "@/app/typing/chat";
import { observer } from "mobx-react-lite";
import ChatObjectsView from "./chatObjectsView";
import { get, keys, set } from "mobx";
import { createPatch } from 'diff';
import { applyDiffs } from "@/components/diffs";

import { ObjectEditorWindow } from "./chatObjectEditorWindow/chatObjectEditorWindow";
import { validateLocaleAndSetLanguage } from "typescript";
import { Client, RequestResult } from "@hey-api/client-fetch";
import { ChatTagList } from "./chatTagList";
import { ChatObjectType } from "@/app/chatState";
import ClientViewChat from "./clientViewChat";
import { scrollIntoContainer } from "@/components/scrollUtils";
import { ArrowLeft } from "@/components/UI/svgs";
import { openHistoryViewer } from "./chatWindow/chatWindowComponents/historyViewer";
/**
 * Returns the artifact key that is currently "active" in the editor:
 * - If the selected object is an artifact directly → return it
 * - If the selected object is an event with a child_index pointing at an artifact → return that child
 * - Otherwise → null
 */
const usageBlockOf = (error: unknown): UsageBlockReason | null =>
    error instanceof SessionCommandError && error.code === "usage_blocked" && isUsageBlockReason(error.reason) ? error.reason : null;

/** Selection the open conversation should send with start / steer. */
export const selectionContextForAgent = () => ({
    selected_objects: chatState.getSelected(),
    open_object: getActiveArtifactKey() ?? chatState.selectedObjectKey ?? undefined,
});

export const getActiveArtifactKey = (): { type: "artifact"; id: number } | null => {
    const sel = chatState.selectedObjectKey;
    if (!sel) return null;
    if (sel.type === "artifact") return { type: "artifact", id: sel.id };
    if (sel.type === "event") {
        const eventObj = chatState.chatObjects.get(sel);
        const childIndex = (eventObj as any)?.child_index;
        if (childIndex !== undefined) {
            const treeNodes = chatState.chatObjects.entries([sel as any]);
            const treeNode = treeNodes.length > 0 ? treeNodes[0] : undefined;
            const child = treeNode?.children?.[childIndex];
            if (child?.key.type === "artifact") {
                return { type: "artifact", id: child.key.id };
            }
        }
    }
    return null;
};

export const computeUserInputChangesOnArtifact = (overrideKey?: { type: ChatObjectType; id: number } | null) => {
    // Resolve the artifact key: use override if provided, otherwise derive from selection
    const artifactKey = overrideKey !== undefined ? overrideKey : getActiveArtifactKey();

    // Check if we have unsaved changes in the editor
    if (!artifactKey || !chatState.chatObjects.get(artifactKey)?.temp_obj) {
        return undefined;
    }

    const curr_obj = chatState.chatObjects.get(artifactKey);
    const temp_obj = curr_obj?.temp_obj as { variable_name?: string, content?: string, tag_ids?: number[] } | undefined;
    // Must be an artifact with content
    if (!curr_obj || curr_obj.type !== "artifact" || !("content" in curr_obj.artifact)) {
        console.log("❌ EXIT 2: Invalid object", {
            hasObject: !!curr_obj,
            objectType: curr_obj?.type,
            hasContent: curr_obj?.type === "artifact" && "content" in curr_obj.artifact
        });
        return undefined;
    }


    const artifact = curr_obj.artifact;
    const revisions = curr_obj.revisions || [];

    console.log("📊 Artifact info:", {
        artifactId: artifact.id,
        revisionsCount: revisions.length,
        selectedRevisionId: curr_obj.revision_index
    });

    // If revisions exist, ensure we're viewing the latest revision
    if (revisions.length > 0) {
        const latestRevisionIndex = revisions.filter(x => x !== undefined).length - 1;

        // Check if selectedObjectKey is pointing to the latest revision
        if (curr_obj.revision_index !== latestRevisionIndex) {
            // Not viewing the latest revision, don't send unsaved changes
            console.log("❌ EXIT 3: Not viewing latest revision", {
                currentRevisionId: curr_obj.revision_index,
                latestRevisionIndex: latestRevisionIndex
            });
            return undefined;
        }
    }

    // Determine the base content to compare against
    let baseContent: string;
    let newContent: string;
    let baseVariableName: string;
    let baseTagIds: number[];

    if (revisions.length > 0) {
        console.log("🔄 Using revisions path");
        // Sort revisions and apply all diffs to get the actual current state
        const sortedRevisions = [...revisions].sort((a, b) => a.id - b.id).filter(x => x ? true : false);
        const revisionPage = curr_obj.revision_index ?? -1;
        const maxIndex = revisionPage + 1;
        const isLastRevision = maxIndex == sortedRevisions.length
        const revStrings = sortedRevisions.slice(0, maxIndex).map(x => x?.content_diff) ?? [];
        const root = artifact.content ?? "";


        baseContent = applyDiffs(root, revStrings);
        newContent = (isLastRevision && temp_obj && temp_obj.content) ? temp_obj.content : baseContent;
        // Get metadata from latest revision
        const latestRevision = sortedRevisions[revisionPage];
        baseVariableName = latestRevision?.variable_name ?? artifact.variable_name;
        baseTagIds = latestRevision?.tag_ids ?? artifact.tag_ids;

        console.log("📝 Computed from revisions:", {
            revisionPage,
            maxIndex,
            diffsApplied: revStrings.length,
            baseContentLength: baseContent.length
        });
    } else {
        console.log("📄 Using base artifact (no revisions)");
        // No revisions, compare against the artifact itself
        baseContent = artifact.content;
        baseVariableName = artifact.variable_name;
        baseTagIds = artifact.tag_ids;
        newContent = (temp_obj && temp_obj.content) ? temp_obj.content : baseContent;
    }

    const contentChanged = baseContent !== newContent;
    const variableNameChanged = temp_obj?.variable_name && (temp_obj?.variable_name !== baseVariableName);
    const tagIdsChanged = temp_obj?.tag_ids && temp_obj.tag_ids.sort().toString() !== baseTagIds.sort().toString();
    // Check if there are actual changes


    // Only return update object if there are changes
    if (!contentChanged && !variableNameChanged && !tagIdsChanged) {
        console.log("❌ EXIT 4: No changes detected");
        return undefined;
    }

    // Generate diff only if content changed
    let contentDiff: string | null = null;
    if (contentChanged) {
        contentDiff = createPatch(
            "",
            baseContent,
            newContent,
            "original",
            "updated"
        );
    }

    const result = {
        tag_ids: tagIdsChanged ? temp_obj?.tag_ids?.sort() ?? [] : null, // null means no change, [] means remove all tags
        variable_name: variableNameChanged ? temp_obj?.variable_name ?? "" : null, // none means no change
        content_diff: contentDiff // undefined means no change
    };

    console.log("✅ EXIT 5: Returning update object", result);
    return result;
};

const AdminChat = observer(({ tagIdsPart }: { tagIdsPart: string }) => {
    const tagIds = useMemo(() => tagIdsPart ? tagIdsPart.split('-').map(id => parseInt(id)).filter(id => !isNaN(id)) : [], [tagIdsPart]);

    const getChatAbortController = useRef<AbortController | null>(null);
    // Session messages for this scope that arrive before its history is loaded.
    const historyLoadedRef = useRef(false);
    const pendingSessionRef = useRef<SessionMessage[]>([]);
    const [mViewState, setMViewState] = useState<"left" | "center" | "right">("center");


    const loadingRef = useRef(false);
    const setLoading = (value: boolean) => {
        console.log("Setting loading to", value);
        loadingRef.current = value;
    };
    const [editorWidth, setEditorWidth] = useState(800);
    const parentRef = useRef<HTMLDivElement>(null);

    const handlePanelResize = useCallback((_borderIndex: number, delta: number) => {
        if (parentRef.current) {
            const width = parentRef.current.getBoundingClientRect().width;
            setEditorWidth(prev => Math.max(400, width - 1300, Math.min(width - 700, prev + delta)));
        }
    }, []);

    useEffect(() => {
        const observer = new ResizeObserver(() => {
            if (parentRef.current) {
                const width = parentRef.current.getBoundingClientRect().width;
                setEditorWidth(prev => Math.max(400, width - 1300, Math.min(width - 700, prev)));
            }
        });

        if (parentRef.current) {
            observer.observe(parentRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [scrollRefIsAtBottom, setScrollRefIsAtBottom] = useState(true);

    // iOS Safari stops rendering sticky elements inside a column-reverse scroller when
    // the keyboard shows or hides. On mobile only the messages scroll, so nothing is
    // sticky; scrollRef points at whichever element is the conversation scroller.
    const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
    useEffect(() => {
        const mql = window.matchMedia("(max-width: 767px)");
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const handleScroll = () => {
            setScrollRefIsAtBottom(-el.scrollTop < 50); // 50px threshold
        }
        el.addEventListener('scroll', handleScroll);
        return () => {
            el.removeEventListener('scroll', handleScroll);
        };

    }, [scrollRef.current, isMobile])

    useEffect(() => {
        // Abort any pending getChat request
        if (getChatAbortController.current) {
            console.log("Aborting previous getChat request");
            getChatAbortController.current.abort();
        }
        // Runs keep going server-side; switching scope only moves the stream focus.
        historyLoadedRef.current = false;
        pendingSessionRef.current = [];
        setMViewState("center");
        setLoading(false);

        chatSession.subscribe(tagIds);
        _getChat();
        chatState.setMounted(true)

        return () => {
            // Clear state on tag switch — runs during cleanup phase (before child setup effects)
            // so chatObjectsView's getArtifacts sees fresh objects_maxpage
            chatState.clear();
        }
    }, [tagIdsPart])

    const isToolResult = (message: ChatMessage) => {
        return message.content instanceof Array && message.content.length > 0 && message.content[0]['type'] === 'tool_result';
    };


    useEffect(() => {
        setMViewState("center");
    }, [chatState.selectedObjectKey])

    const scrollToLatest = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    useEffect(() => {
        return chatSession.addListener((message) => {
            if (!isScopedSessionMessage(message)) return;
            if (!historyLoadedRef.current) {
                pendingSessionRef.current.push(message);
                return;
            }
            applySessionMessage(message, scrollToLatest);
        });
    }, [scrollToLatest]);

    const flushPendingSession = () => {
        historyLoadedRef.current = true;
        const pending = pendingSessionRef.current;
        pendingSessionRef.current = [];
        pending.forEach((message) => applySessionMessage(message, scrollToLatest));
    };

    const _getChat = async () => {

        // Create a new AbortController for this specific request
        const requestAbortController = new AbortController();
        getChatAbortController.current = requestAbortController;

        try {

            const response: any = await getChat({
                body: { tag_ids: tagIds },
            });

            // Check if this request was aborted (another request started)
            if (getChatAbortController.current !== requestAbortController) {
                console.log("Request superseded by newer request");
                return;
            }

            if (response.data) {
                const greet = response.data.message == "conversation does not exist" && !runsState.forScope(tagIds);

                const _conversation: ConversationBlock[] = [];

                (response.data.conversation as ChatMessage[])?.forEach((message: ChatMessage) => {
                    if (message.is_injection)
                        return
                    const lastBlock = _conversation[_conversation.length - 1];
                    if (lastBlock && ((lastBlock.role === message.role && lastBlock.agent_id == message.agent_id) || isToolResult(message))) {
                        lastBlock.content.push({ ...message, conversation_id: response.data.conversation_id });
                    } else {
                        _conversation.push({ role: message.role, agent_id: message.agent_id, content: [{ ...message, conversation_id: response.data.conversation_id }] });
                    }
                });

                chatState.setConversation(_conversation, response.data.conversation_id, tagIds);
                chatState.setEarlier(response.data.memories, response.data.history);
                runsState.markSeen(response.data.conversation_id);
                flushPendingSession();

                chatState.commitDelayedSelectedObjectKey();
                if (greet) handleSend("please greet the user", true);

            } else if (response.error) {
                console.error("Error occurred:", response);
            }
        } catch (error: any) {
            // Handle abort errors silently
            if (error.name === 'AbortError') {
                console.log("Fetch was aborted successfully");
                return;
            }
            console.error("Error in _getChat:", error);
        } finally {
            setLoading(false);
            if (getChatAbortController.current === requestAbortController && !historyLoadedRef.current) {
                flushPendingSession();
            }
        }
    }


    const handleSend = useCallback(async (request: string, is_injection?: boolean, replay_message_id?: number, skill_key?: string) => {
        // Prevent multiple concurrent sends
        console.log("HANDESEEEND", "loading", loadingRef.current)
        if (loadingRef.current) return;

        if (!skill_key && request.length > 5000) {
            appState.setModal({
                title: appState.t("chat.inputTooLongTitle"),
                body: appState.t("chat.inputTooLongBody"),
            });
            return;
        }

        if (replay_message_id) {
            const newConv: ConversationBlock[] = [];
            let found = false;
            for (const block of chatState.conversation) {
                if (found) break;

                const msgIndex = block.content.findIndex(m => m.id === replay_message_id);
                if (msgIndex !== -1) {
                    const newContent = block.content.slice(0, msgIndex + 1);
                    newConv.push({ ...block, content: newContent });
                    found = true;
                } else {
                    newConv.push(block);
                }
            }
            chatState.setConversation(newConv);
        }

        setLoading(true);
        if (!is_injection && !replay_message_id && !skill_key)
            chatState.addTempUserMessage(request);
        chatState.setState("awaiting_backend");
        scrollToLatest();
        try {
            const reply = await chatSession.start({
                tag_ids: tagIds,
                prompt: request,
                is_injection: is_injection,
                ...selectionContextForAgent(),
                artifact_update: computeUserInputChangesOnArtifact(),
                replay_message_id: replay_message_id,
                skill_key: skill_key,
            });
            if (chatState.conversationId === null) {
                chatState.setConversation(chatState.conversation, reply.conversation_id);
            }
            // Spending is allowed again (e.g. the ceiling was raised elsewhere): drop the stale banner.
            if (usageMeterState.blockedReason) void usageMeterState.refresh();
        } catch (error) {
            chatState.setState("idle");
            const blocked = usageBlockOf(error);
            if (blocked) {
                // The composer keeps the text and the banner explains why.
                chatState.dropTempUserMessages();
                usageMeterState.block(blocked);
                return false;
            }
            console.error("Error in handleSend:", error);
            appState.setModal({
                title: appState.t("common.error"),
                body: error instanceof Error ? error.message : String(error),
            });
        } finally {
            setLoading(false);
        }
    }, [tagIdsPart]);

    /** Message sent while the agent runs: queued server-side and picked up at its next step. */
    const steer = useCallback(async (request: string) => {
        const conversationId = chatState.conversationId;
        if (conversationId === null) return;
        if (request.length > 5000) {
            appState.setModal({
                title: appState.t("chat.inputTooLongTitle"),
                body: appState.t("chat.inputTooLongBody"),
            });
            return;
        }
        const queuedId = chatState.addQueuedSteer(request);
        scrollToLatest();
        try {
            const reply = await chatSession.steer(conversationId, request, selectionContextForAgent());
            if (reply.type === "started") {
                // The run ended just before the steer arrived; it started a new turn instead.
                chatState.removeQueuedSteer(queuedId);
                chatState.addTempUserMessage(request);
            }
        } catch (error) {
            chatState.removeQueuedSteer(queuedId);
            const blocked = usageBlockOf(error);
            if (blocked) {
                usageMeterState.block(blocked);
                return false;
            }
            appState.setModal({
                title: appState.t("common.error"),
                body: error instanceof Error ? error.message : String(error),
            });
        }
    }, []);

    const send = useCallback(async (request: string, is_injection?: boolean, replay_message_id?: number, skill_key?: string) => {
        switch (routeSend(runsState.isRunning(chatState.conversationId), { is_injection, replay_message_id, skill_key })) {
            case "steer":
                return steer(request);
            case "start":
                return handleSend(request, is_injection, replay_message_id, skill_key);
        }
    }, [handleSend, steer]);

    const cancel = useCallback(async () => {
        if (chatState.conversationId === null) return;
        try {
            await chatSession.cancel(chatState.conversationId);
        } catch (error) {
            console.error("Cancel failed:", error);
        }
    }, []);

    useEffect(() => {
        const handleScrollToKeyEvent = (e: CustomEvent) => {
            const detail: ScrollToObjectKeyDetail = e.detail;
            const key = detail.key;
            if (key.revision_id !== undefined) {
                const id = `artifact${key.id}-${key.revision_id}`;
                const element = document.getElementById(id);
                if (element) setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                return;
            }
            const id = "message_" + chatState.chatObjects.get(e.detail.key)?.msgId
            const element = document.getElementById(id);
            if (element) setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        }

        const handleHistoryCitationClick = (e: CustomEvent) => {
            if (e.detail?.citationType !== "history") return;
            const { conversation_id, start_time, end_time, cited_text } = e.detail;
            openHistoryViewer({ conversationId: conversation_id, startTime: start_time, endTime: end_time, citedText: cited_text });
        };

        document.addEventListener('scroll-to-object-key', handleScrollToKeyEvent as EventListener);
        document.addEventListener('citation-click', handleHistoryCitationClick as EventListener);

        return () => {
            document.removeEventListener('scroll-to-object-key', handleScrollToKeyEvent as EventListener);
            document.removeEventListener('citation-click', handleHistoryCitationClick as EventListener);
            getChatAbortController.current?.abort();

            console.log("Cleaning up AdminChat, clearing chatState");
            chatState.clear();

        }

    }, [])

    /*
        temp state on object not currently selected, does not work with update artifact, as agent has no knowledge of it
    
        maybe only allow updates on selected items?
        sync temp state with backend?
    
    */
    const [isResizing, setIsResizing] = useState(false);
    const lastClientX = useRef<number>(0);

    const startResizing = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
        lastClientX.current = e.clientX;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, []);

    const stopResizing = useCallback(() => {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }, []);

    const SidePanelBut = ({ isLeftPointing = true, onClick }: { isLeftPointing?: boolean; onClick?: () => void }) => (
        <div
            className="edge-toggle group"
            onClick={onClick}
        >
            <div className={`edge-toggle-chevron ${isLeftPointing ? "" : "edge-toggle-chevron-flipped"}`}>
                <ArrowLeft size={30} />
            </div>
        </div>
    )
    const resize = useCallback((e: MouseEvent) => {
        if (isResizing && parentRef.current) {
            const delta = e.clientX - lastClientX.current;
            const width = parentRef.current.getBoundingClientRect().width;

            setEditorWidth(prev => Math.max(400, width - 1300, Math.min(width - 700, prev + delta)));
            lastClientX.current = e.clientX;
        }
    }, [isResizing]);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', resize);
            window.addEventListener('mouseup', stopResizing);
        }
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [isResizing, resize, stopResizing]);

    const mobileShift = useMemo(() => {
        // No transform when centered: iOS Safari stops painting the sticky composer
        // inside a transformed ancestor once the keyboard opens.
        if (mViewState === "center") return "transform-none";
        if (mViewState === "left") return "translate-x-[10rem]";
        if (mViewState === "right") return "translate-x-[-10rem]";
    }, [mViewState]);

    return (

        <div className="h-dvh w-full flex flex-col items-center overflow-hidden">
            <div className={`h-full w-[calc(100vw+20rem)] md:w-full ${mobileShift} overflow-hidden md:transform-none transition-transform duration-300 ease-in-out`}>

                <div ref={isMobile ? undefined : scrollRef} className="flex flex-col-reverse overflow-hidden md:overflow-y-auto overscroll-contain justify-between max-h-full h-full w-full relative items-center">
                    <div ref={parentRef} className="flex flex-row grow w-full justify-center gap-[0.25rem] max-md:h-full max-md:min-h-0">

                        {chatState.showDebugView && <div
                            className="sticky top-0 h-dvh overflow-y-auto shrink-0"
                        ><ClientViewChat tagIdsPart={tagIdsPart} /></div>}

                        <div className="chat-sidebar left-0 overflow-x-visible pt-nav">
                            <ChatTagList tagIds={tagIds} />
                        </div>
                        <SidePanelBut
                            isLeftPointing={mViewState === "left"}
                            onClick={() => setMViewState(mViewState === "left" ? "center" : "left")}
                        />
                        <div className="hidden md:block md:grow"></div>

                        {chatState.selectedObjectKey !== null && <>
                            <div style={{ width: editorWidth }} className={`hidden md:flex sticky top-0 h-dvh pt-nav max-w-[1024px] flex-row justify-center shrink-0 px-1`}>
                                <ObjectEditorWindow />
                            </div>
                            <div
                                className={`panel-resizer hidden md:flex ${isResizing ? "panel-resizer-active" : ""}`}
                                onMouseDown={startResizing}
                            >
                                <div className="panel-resizer-grip" />
                            </div>
                        </>}



                        <Chat onSendMessage={send} onCancel={cancel} scrollRef={scrollRef} messagesScrollRef={isMobile ? scrollRef : undefined} scrollRefIsAtBottom={scrollRefIsAtBottom} onComposerFocus={() => setMViewState("center")} />
                        <SidePanelBut
                            isLeftPointing={mViewState !== "right"}
                            onClick={() => setMViewState(mViewState === "right" ? "center" : "right")}
                        />

                        <div className="hidden md:block md:grow"></div>

                        <div className="chat-sidebar right-0">
                            <ChatObjectsView tagIds={tagIds} />
                        </div>
                    </div>
                </div>

            </div>

            {chatState.selectedObjectKey !== null && <div className="block md:hidden">
                <div className="sheet">
                    <div
                        onClick={() => {
                            chatState.setSelectedObjectKey(null);
                        }}
                        className="sheet-backdrop"
                    ></div>

                    <div className="flex flex-row justify-center h-dvh w-full" style={{ scrollbarGutter: "auto" }}>
                        <div className="flex flex-col w-full max-w-2xl h-full">
                            <div
                                onClick={(e) => e.stopPropagation()}
                                className="sheet-body"
                            >
                                <ObjectEditorWindow />
                            </div>
                        </div>
                    </div>
                </div>
            </div>}

        </div>

    );
});

export default AdminChat;